import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ExternalLink, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import type { DiretoriaOficinaProdutividade } from '@/lib/diretoriaOficinaFetch';
import { formatPeriodLabel } from '@/lib/diretoriaPeriod';
import { DIRETORIA_OFICINA_QUERY_IDS } from '@/lib/diretoriaQueryIds';
import type { DiretoriaQuerySource } from '@/hooks/useDiretoriaSqlCatalog';
import { exportTableCsv } from '@/lib/riqueExport';

interface KpiMiniProps {
  label: string;
  value: string | number;
  tone?: 'good' | 'bad' | 'neutral';
  hint?: string;
}

function KpiMini({ label, value, tone = 'neutral', hint }: KpiMiniProps) {
  return (
    <Card className="border-border/80 shadow-soft-sm">
      <CardHeader className="pb-1 pt-3">
        <CardTitle className="text-[11px] font-medium leading-tight text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="pb-3 pt-0">
        <p className={cn('font-heading text-xl font-bold tabular-nums', diretoriaToneTextClass(tone))}>{value}</p>
        {hint ? <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export interface DiretoriaOficinaSectionProps {
  data: DiretoriaOficinaProdutividade | null | undefined;
  isLoading?: boolean;
  companyLabel: string;
  resolveSql?: (queryId: string) => { sql?: string; source?: DiretoriaQuerySource; title?: string };
  dataFrom: string;
  dataTo: string;
}

export const DiretoriaOficinaSection: React.FC<DiretoriaOficinaSectionProps> = ({
  data,
  isLoading,
  companyLabel,
  resolveSql,
  dataFrom,
  dataTo,
}) => {
  const fmtPct = (v: number | null) => (v == null ? '—' : `${v}%`);
  const fmtMin = (v: number | null) => (v == null ? '—' : `${v} min`);

  const locacaoChartData =
    data?.charts.locacaoPorEmpresa.map((r) => ({
      name: r.empresa.length > 18 ? `${r.empresa.slice(0, 16)}…` : r.empresa,
      emDia: r.emDia,
      atrasada: r.atrasada,
    })) ?? [];

  return (
    <section className="space-y-4" aria-labelledby="diretoria-oficina-heading">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-muted-foreground">
            <Wrench className="h-4 w-4" aria-hidden />
            <span className="text-xs font-medium uppercase tracking-wide">Operação</span>
          </div>
          <h2 id="diretoria-oficina-heading" className="font-heading text-lg font-semibold text-foreground">
            Produtividade da Oficina
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {companyLabel} · período {formatPeriodLabel(dataFrom, dataTo)}
            {data?.updatedAt ? ` · atualizado ${new Date(data.updatedAt).toLocaleString('pt-BR')}` : ''}
          </p>
        </div>
        <Link to="/dashboard-oficina">
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Painel Oficina
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando produtividade da oficina…</p>
      ) : !data ? (
        <p className="rounded-lg border border-border/80 bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
          Dados da oficina indisponíveis para o filtro atual.
        </p>
      ) : (
        <>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Hoje</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <KpiMini label="OS realizadas hoje" value={data.hoje.osRealizadas} tone="good" />
              <KpiMini label="Agendamentos concluídos hoje" value={data.hoje.agendamentosConcluidos} tone="good" />
              <KpiMini label="OS em execução (WIP)" value={data.wip.emExecucao} />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Agendamentos (período)</p>
              <DiretoriaSqlButton {...(resolveSql?.(DIRETORIA_OFICINA_QUERY_IDS.agendamentos) ?? {})} />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <KpiMini label="Total agendamentos" value={data.agendamentos.total} />
              <KpiMini label="Concluídos" value={data.agendamentos.concluidos} tone="good" />
              <KpiMini
                label="No-show"
                value={data.agendamentos.noShow}
                tone={data.agendamentos.noShow > 0 ? 'bad' : 'good'}
                hint={fmtPct(data.agendamentos.taxaComparecimento) !== '—' ? `Comparecimento ${fmtPct(data.agendamentos.taxaComparecimento)}` : undefined}
              />
              <KpiMini label="Tempo médio" value={fmtMin(data.agendamentos.tempoMedioMinutos)} />
              <KpiMini label="NPS" value={data.agendamentos.npsScore ?? '—'} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Locação e WIP</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <KpiMini label="Serviços loc. em dia" value={data.locacaoPeriodo.emDia} tone="good" />
              <KpiMini label="Serviços loc. atrasada" value={data.locacaoPeriodo.atrasada} tone="bad" />
              <KpiMini label="Média dias na oficina" value={data.wip.mediaDias} />
              <KpiMini label="OS críticas (WIP)" value={data.wip.criticas} tone={data.wip.criticas > 0 ? 'bad' : 'good'} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="border-border/80">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">Produtividade por técnico</CardTitle>
                  <DiretoriaSqlButton {...(resolveSql?.(DIRETORIA_OFICINA_QUERY_IDS.porTecnico) ?? {})} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-[280px] w-full">
                  {data.charts.porTecnico.length === 0 ? (
                    <p className="flex h-full items-center justify-center text-xs text-muted-foreground">
                      Sem serviços concluídos no período.
                    </p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data.charts.porTecnico}
                        layout="vertical"
                        margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.3} />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="tecnico" width={100} tick={{ fontSize: 9 }} />
                        <Tooltip formatter={(v: number) => [v, 'Serviços']} />
                        <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 3, 3, 0]} name="Serviços" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/80">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">Locação por empresa</CardTitle>
                  <DiretoriaSqlButton {...(resolveSql?.(DIRETORIA_OFICINA_QUERY_IDS.locacaoEmpresa) ?? {})} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-[280px] w-full">
                  {locacaoChartData.length === 0 ? (
                    <p className="flex h-full items-center justify-center text-xs text-muted-foreground">
                      Sem serviços com status de locação no período.
                    </p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={locacaoChartData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={48} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={32} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="emDia" stackId="a" fill="#16a34a" name="Em dia" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="atrasada" stackId="a" fill="#e11d48" name="Atrasada" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="border-border/80">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">OS por técnico e tipo de serviço</CardTitle>
                <button
                  type="button"
                  className="text-[10px] font-medium text-primary hover:underline"
                  onClick={() => {
                    const rows = data.tables.tecnicoPorTipo.map((r) => [
                      r.tecnico,
                      String(r.expresso),
                      String(r.intermediario),
                      String(r.tipoTecnico),
                      String(r.setorOutros),
                      String(r.total),
                    ]);
                    exportTableCsv(
                      'tecnico-tipo-servico',
                      ['Técnico', 'Expresso', 'Intermediário', 'Técnico', 'Outros setores', 'Total'],
                      rows
                    );
                  }}
                >
                  Exportar CSV
                </button>
              </CardHeader>
              <CardContent className="overflow-x-auto pt-0">
                <table className="w-full min-w-[32rem] text-xs">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-1 pr-2 font-medium">Técnico</th>
                      <th className="px-1 py-1 font-medium">Expresso</th>
                      <th className="px-1 py-1 font-medium">Intermediário</th>
                      <th className="px-1 py-1 font-medium">Técnico</th>
                      <th className="px-1 py-1 font-medium">Outros</th>
                      <th className="py-1 pl-1 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tables.tecnicoPorTipo.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-muted-foreground">
                          Sem dados no período.
                        </td>
                      </tr>
                    ) : (
                      data.tables.tecnicoPorTipo.map((row) => (
                        <tr key={row.tecnico} className="border-b border-border/50">
                          <td className="py-1 pr-2 font-medium">{row.tecnico}</td>
                          <td className="px-1 py-1 tabular-nums">{row.expresso}</td>
                          <td className="px-1 py-1 tabular-nums">{row.intermediario}</td>
                          <td className="px-1 py-1 tabular-nums">{row.tipoTecnico}</td>
                          <td className="px-1 py-1 tabular-nums">{row.setorOutros}</td>
                          <td className="py-1 pl-1 font-semibold tabular-nums">{row.total}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="border-border/80">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Serviços por empresa e locação</CardTitle>
                <button
                  type="button"
                  className="text-[10px] font-medium text-primary hover:underline"
                  onClick={() => {
                    const rows = data.tables.locacaoPorEmpresa.map((r) => [
                      r.empresa,
                      String(r.emDia),
                      String(r.atrasada),
                      String(r.total),
                      `${r.pctAtrasada}%`,
                    ]);
                    exportTableCsv(
                      'locacao-empresa',
                      ['Empresa', 'Em dia', 'Atrasada', 'Total', '% atrasada'],
                      rows
                    );
                  }}
                >
                  Exportar CSV
                </button>
              </CardHeader>
              <CardContent className="overflow-x-auto pt-0">
                <table className="w-full min-w-[28rem] text-xs">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-1 pr-2 font-medium">Empresa</th>
                      <th className="px-1 py-1 font-medium">Em dia</th>
                      <th className="px-1 py-1 font-medium">Atrasada</th>
                      <th className="px-1 py-1 font-medium">Total</th>
                      <th className="py-1 pl-1 font-medium">% atrasada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tables.locacaoPorEmpresa.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-4 text-center text-muted-foreground">
                          Sem dados no período.
                        </td>
                      </tr>
                    ) : (
                      data.tables.locacaoPorEmpresa.map((row) => (
                        <tr key={row.empresaId} className="border-b border-border/50">
                          <td className="py-1 pr-2 font-medium">{row.empresa}</td>
                          <td className="px-1 py-1 tabular-nums text-emerald-700 dark:text-emerald-400">{row.emDia}</td>
                          <td className="px-1 py-1 tabular-nums text-rose-700 dark:text-rose-400">{row.atrasada}</td>
                          <td className="px-1 py-1 tabular-nums">{row.total}</td>
                          <td className="py-1 pl-1 tabular-nums">{row.pctAtrasada}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </section>
  );
};
